import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { advisorApiService } from "../services/advisor-api";
import { useAuth } from "../contexts/AuthContext";
import { ADMIN_ROUTES, ADMIN_AUTH_ROUTES } from "../constants/routes";
import type {
  AdminDashboardResponse,
  QuestionAnswerRecord,
  StudentFeedbackRecord,
} from "../types";
import { StudentFeedbackDashboard } from "./StudentFeedbackDashboard";
import { FeedbackDetailModal } from "./FeedbackDetailModal";
import { QAManagementDashboard } from "./QAManagement/QAManagementDashboard";
import { MetlasIntelligencePanel } from "./MetlasIntelligencePanel";
import { formatUsername, isAnonymousUsername } from "../utils/visitor-display";
import "../styles/AdvisorAdmin.css";

type DashboardView = "overview" | "student-support" | "qa-management";
type SortOrder = "asc" | "desc";

interface AdvisorAdminProps {
  view: DashboardView;
}

/** Sentinel filter values for the user filter's "kind" quick filters.
 * Underscored to avoid collision with any real username. */
const USER_FILTER_ALL = "all";
const USER_FILTER_SIGNED_IN = "__signed_in__";
const USER_FILTER_ANONYMOUS = "__anonymous__";

const AdvisorAdmin: React.FC<AdvisorAdminProps> = ({ view }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] =
    useState<AdminDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(100);
  const [inputLimit, setInputLimit] = useState("100");

  // Sorting and filtering state
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [programFilter, setProgramFilter] = useState<string>("all");
  const [ratedFilter, setRatedFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>(USER_FILTER_ALL);

  // Dropdown visibility state
  const [showProgramFilter, setShowProgramFilter] = useState(false);
  const [showRatedFilter, setShowRatedFilter] = useState(false);
  const [showUserFilter, setShowUserFilter] = useState(false);

  // Expanded answer modal state
  const [expandedAnswer, setExpandedAnswer] =
    useState<QuestionAnswerRecord | null>(null);

  // Feedback detail modal state
  const [selectedFeedback, setSelectedFeedback] =
    useState<StudentFeedbackRecord | null>(null);

  // The floating Metlas Intelligence widget manages its own open/close state
  // and date window; it only needs the current program filter from this page.
  // Metlas Intelligence is available to USER and ADMIN roles (not READER),
  // matching the backend requireRole([USER, ADMIN]) on its endpoints.
  const canUseMetlas = user?.role === "admin" || user?.role === "user";

  const fetchDashboardData = async (questionLimit: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await advisorApiService.getAdminData(questionLimit);
      if (response.data) {
        setDashboardData(response.data);
      } else {
        setError(response.error || "Failed to fetch dashboard data");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData(limit);
  }, [limit]);

  const handleLimitChange = () => {
    const newLimit = parseInt(inputLimit, 10);
    if (newLimit >= 1 && newLimit <= 1000) {
      setLimit(newLimit);
      fetchDashboardData(newLimit);
    } else {
      alert("Limit must be between 1 and 1000");
    }
  };

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    if (!dashboardData) return { programs: [], ratings: [], users: [] };

    const programs = Array.from(
      new Set(dashboardData.questions.map((q) => q.program_code || "-")),
    ).sort();
    const ratings = Array.from(
      new Set(
        dashboardData.questions.map((q) => {
          if (q.is_relevant === true) return "Yes";
          if (q.is_relevant === false) return "No";
          return "Unrated";
        }),
      ),
    ).sort();
    // Sort signed-in usernames first (alpha), then anonymous identifiers
    // (so the human-readable accounts cluster at the top of the dropdown
    // instead of being buried in a wall of anon_<uuid> entries).
    const users = Array.from(
      new Set(dashboardData.questions.map((q) => q.username || "-")),
    ).sort((a, b) => {
      const aAnon = isAnonymousUsername(a);
      const bAnon = isAnonymousUsername(b);
      if (aAnon !== bAnon) return aAnon ? 1 : -1;
      return a.localeCompare(b);
    });

    return { programs, ratings, users };
  }, [dashboardData]);

  // Apply sorting and filtering
  const filteredAndSortedQuestions = useMemo(() => {
    if (!dashboardData) return [];

    let filtered = [...dashboardData.questions];

    // Apply program filter
    if (programFilter !== "all") {
      filtered = filtered.filter((q) => {
        const program = q.program_code || "-";
        return program === programFilter;
      });
    }

    // Apply rated filter
    if (ratedFilter !== "all") {
      filtered = filtered.filter((q) => {
        let rating = "Unrated";
        if (q.is_relevant === true) rating = "Yes";
        if (q.is_relevant === false) rating = "No";
        return rating === ratedFilter;
      });
    }

    // Apply user filter. Three quick-filter sentinels are handled first
    // (signed-in only, anonymous only). Any other value is treated as a
    // raw username to exactly match.
    if (userFilter === USER_FILTER_SIGNED_IN) {
      filtered = filtered.filter(
        (q) => !!q.username && !isAnonymousUsername(q.username),
      );
    } else if (userFilter === USER_FILTER_ANONYMOUS) {
      filtered = filtered.filter((q) => isAnonymousUsername(q.username));
    } else if (userFilter !== USER_FILTER_ALL) {
      filtered = filtered.filter((q) => (q.username || "-") === userFilter);
    }

    // Apply sorting by created date
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_time).getTime();
      const dateB = new Date(b.created_time).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  }, [dashboardData, programFilter, ratedFilter, userFilter, sortOrder]);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(ADMIN_AUTH_ROUTES.LOGIN);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const renderSidebar = () => (
    <div className="admin-sidebar">
      <div className="admin-logo">
        <img src="/columbia_university_head.png" alt="Columbia University" />
        <h2>Advising Chatbot Admin</h2>
      </div>
      <nav className="admin-nav">
        <Link
          to={ADMIN_ROUTES.OVERVIEW}
          className={view === "overview" ? "active" : ""}
        >
          Question Answer Overview
        </Link>
        <Link
          to={ADMIN_ROUTES.SUPPORT}
          className={view === "student-support" ? "active" : ""}
        >
          Student Support
        </Link>
        <Link
          to={ADMIN_ROUTES.QA_MANAGEMENT}
          className={view === "qa-management" ? "active" : ""}
        >
          Question Answer Management
        </Link>
      </nav>
      <div className="admin-user-info">
        <div className="user-details">
          <div className="user-name">{user?.username}</div>
          <div className="user-role">{user?.role}</div>
        </div>
        <button onClick={handleLogout} className="logout-button">
          Logout
        </button>
      </div>
    </div>
  );

  const renderOverview = () => {
    if (isLoading) {
      return <div className="admin-loading">Loading dashboard data...</div>;
    }

    if (error) {
      return (
        <div className="error-box-wrap">
          <div className="error-box">
            <h3>Error</h3>
            <p>{error}</p>
          </div>
        </div>
      );
    }

    if (!dashboardData) {
      return (
        <div className="error-box-wrap">
          <div className="error-box">
            <h3>Error</h3>
            <p>No data available</p>
          </div>
        </div>
      );
    }

    const ratingPercentage =
      dashboardData.totalQuestions > 0
        ? (
            (dashboardData.ratedQuestions / dashboardData.totalQuestions) *
            100
          ).toFixed(1)
        : "0.0";

    return (
      <div className="admin-overview">
        <div className="admin-overview-header">
          <h1>Question Answer Overview</h1>
          <div className="admin-overview-controls">
            <label>
              Results:
              <input
                type="number"
                value={inputLimit}
                onChange={(e) => setInputLimit(e.target.value)}
                min="1"
                max="1000"
                className="limit-input"
              />
            </label>
            <button onClick={handleLimitChange} className="refresh-button">
              Apply
            </button>
            <button
              onClick={() => fetchDashboardData(limit)}
              className="refresh-button"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="admin-stats">
          <div className="stat-card">
            <div className="stat-value">
              {dashboardData.totalQuestions.toLocaleString()}
            </div>
            <div className="stat-label">Total Questions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {dashboardData.ratedQuestions.toLocaleString()}
            </div>
            <div className="stat-label">Rated Questions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{ratingPercentage}%</div>
            <div className="stat-label">Rating Coverage</div>
          </div>
        </div>

        <div className="questions-table-container">
          <h2>
            Recent Questions (Showing {filteredAndSortedQuestions.length} of{" "}
            {dashboardData.questions.length})
          </h2>
          <table className="questions-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={toggleSortOrder}>
                  <div className="header-content">
                    Created {sortOrder === "asc" ? "↑" : "↓"}
                  </div>
                </th>
                <th className="filterable-header">
                  <div className="header-content">
                    User
                    <button
                      className="filter-icon"
                      onClick={() => setShowUserFilter(!showUserFilter)}
                      title="Filter by user"
                    >
                      {userFilter !== USER_FILTER_ALL ? "🔽" : "▼"}
                    </button>
                  </div>
                  {showUserFilter && (
                    <div className="filter-dropdown">
                      {/* Quick filters: all visitors, only signed-in (admins),
                          only anonymous. Underscored sentinels never collide
                          with a real username. */}
                      <div
                        className={`filter-option ${userFilter === USER_FILTER_ALL ? "selected" : ""}`}
                        onClick={() => {
                          setUserFilter(USER_FILTER_ALL);
                          setShowUserFilter(false);
                        }}
                      >
                        All
                      </div>
                      <div
                        className={`filter-option ${userFilter === USER_FILTER_SIGNED_IN ? "selected" : ""}`}
                        onClick={() => {
                          setUserFilter(USER_FILTER_SIGNED_IN);
                          setShowUserFilter(false);
                        }}
                      >
                        Signed-in only
                      </div>
                      <div
                        className={`filter-option ${userFilter === USER_FILTER_ANONYMOUS ? "selected" : ""}`}
                        onClick={() => {
                          setUserFilter(USER_FILTER_ANONYMOUS);
                          setShowUserFilter(false);
                        }}
                      >
                        Anonymous only
                      </div>
                      <div className="filter-option-divider" />
                      {/* Individual usernames — anonymous rows rendered as
                          "Anonymous #xxxxxxxx" with the first 8 hex chars
                          of the UUID for spotting same-visitor clusters. */}
                      {filterOptions.users.map((user) => {
                        const { display } = formatUsername(user);
                        return (
                          <div
                            key={user}
                            className={`filter-option ${userFilter === user ? "selected" : ""}`}
                            onClick={() => {
                              setUserFilter(user);
                              setShowUserFilter(false);
                            }}
                            title={user}
                          >
                            {display}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </th>
                <th>Question</th>
                <th>Matched Question</th>
                <th>Answer Preview</th>
                <th className="filterable-header">
                  <div className="header-content">
                    Program
                    <button
                      className="filter-icon"
                      onClick={() => setShowProgramFilter(!showProgramFilter)}
                      title="Filter by program"
                    >
                      {programFilter !== "all" ? "🔽" : "▼"}
                    </button>
                  </div>
                  {showProgramFilter && (
                    <div className="filter-dropdown">
                      <div
                        className={`filter-option ${programFilter === "all" ? "selected" : ""}`}
                        onClick={() => {
                          setProgramFilter("all");
                          setShowProgramFilter(false);
                        }}
                      >
                        All
                      </div>
                      {filterOptions.programs.map((program) => (
                        <div
                          key={program}
                          className={`filter-option ${programFilter === program ? "selected" : ""}`}
                          onClick={() => {
                            setProgramFilter(program);
                            setShowProgramFilter(false);
                          }}
                        >
                          {program}
                        </div>
                      ))}
                    </div>
                  )}
                </th>
                <th>Visa</th>
                <th className="filterable-header">
                  <div className="header-content">
                    Rated
                    <button
                      className="filter-icon"
                      onClick={() => setShowRatedFilter(!showRatedFilter)}
                      title="Filter by rating"
                    >
                      {ratedFilter !== "all" ? "🔽" : "▼"}
                    </button>
                  </div>
                  {showRatedFilter && (
                    <div className="filter-dropdown">
                      <div
                        className={`filter-option ${ratedFilter === "all" ? "selected" : ""}`}
                        onClick={() => {
                          setRatedFilter("all");
                          setShowRatedFilter(false);
                        }}
                      >
                        All
                      </div>
                      {filterOptions.ratings.map((rating) => (
                        <div
                          key={rating}
                          className={`filter-option ${ratedFilter === rating ? "selected" : ""}`}
                          onClick={() => {
                            setRatedFilter(rating);
                            setShowRatedFilter(false);
                          }}
                        >
                          {rating}
                        </div>
                      ))}
                    </div>
                  )}
                </th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedQuestions.map((q: QuestionAnswerRecord) => (
                <tr key={q.id}>
                  <td className="cell-date">
                    {new Date(q.created_time).toLocaleString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td
                    className={`cell-user${formatUsername(q.username).isAnonymous ? " cell-user-anon" : ""}`}
                    title={q.username || ""}
                  >
                    {formatUsername(q.username).display}
                  </td>
                  <td className="cell-question">{q.question}</td>
                  <td className="cell-matched">{q.matched_question || "-"}</td>
                  <td
                    className="cell-answer clickable"
                    onClick={() => setExpandedAnswer(q)}
                    title="Click to view full answer"
                  >
                    {q.answer
                      .replace(/<[^>]*>/g, " ")
                      .replace(/\s+/g, " ")
                      .trim()
                      .substring(0, 150) + (q.answer.length > 150 ? "..." : "")}
                  </td>
                  <td className="cell-program">{q.program_code || "-"}</td>
                  <td className="cell-visa">
                    {q.student_context?.visa_restriction === "No"
                      ? "No"
                      : q.student_context?.visa_restriction !== "No"
                        ? "Yes"
                        : "-"}
                  </td>
                  <td className="cell-rating">
                    {q.is_relevant === true ? (
                      <span className="badge-relevant">Yes</span>
                    ) : q.is_relevant === false ? (
                      <span className="badge-not-relevant">No</span>
                    ) : (
                      <span className="badge-unrated">-</span>
                    )}
                  </td>
                  <td className="cell-notes">{q.researcher_notes || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (view) {
      case "overview":
        return renderOverview();
      case "student-support":
        return (
          <StudentFeedbackDashboard onFeedbackClick={setSelectedFeedback} />
        );
      case "qa-management":
        return <QAManagementDashboard />;
      default:
        return renderOverview();
    }
  };

  return (
    <div className="advisor-admin">
      {renderSidebar()}
      <div className="admin-content">{renderContent()}</div>

      {/* Answer Expansion Modal */}
      {expandedAnswer && (
        <div className="modal-overlay" onClick={() => setExpandedAnswer(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Full Answer</h2>
              <button
                className="modal-close"
                onClick={() => setExpandedAnswer(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-section">
                <h3>Question</h3>
                <p>{expandedAnswer.question}</p>
              </div>
              {expandedAnswer.matched_question && (
                <div className="modal-section">
                  <h3>Matched Question</h3>
                  <p>{expandedAnswer.matched_question}</p>
                </div>
              )}
              <div className="modal-section">
                <h3>Answer</h3>
                <div
                  className="modal-answer"
                  dangerouslySetInnerHTML={{ __html: expandedAnswer.answer }}
                />
              </div>
              {expandedAnswer.student_context && (
                <div className="modal-section">
                  <h3>Student Context</h3>
                  <div className="modal-student-context">
                    {Object.entries(expandedAnswer.student_context).map(
                      ([key, value]) => (
                        <div key={key} className="context-item">
                          <span className="context-label">
                            {key.replace(/_/g, " ")}:
                          </span>
                          <span className="context-value">{String(value)}</span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
              {expandedAnswer.source_uri && (
                <div className="modal-section">
                  <h3>Source</h3>
                  <p className="modal-source">{expandedAnswer.source_uri}</p>
                </div>
              )}
              {expandedAnswer.program_code && (
                <div className="modal-section">
                  <h3>Program Code</h3>
                  <p className="modal-program">{expandedAnswer.program_code}</p>
                </div>
              )}
              <div className="modal-section">
                <h3>Rating</h3>
                <p>
                  {expandedAnswer.is_relevant === true
                    ? "Relevant (Yes)"
                    : expandedAnswer.is_relevant === false
                      ? "Not Relevant (No)"
                      : "Unrated"}
                </p>
              </div>
              {expandedAnswer.researcher_notes && (
                <div className="modal-section">
                  <h3>Researcher Notes</h3>
                  <p>{expandedAnswer.researcher_notes}</p>
                </div>
              )}
              <div className="modal-section">
                <h3>Created</h3>
                <p>
                  {new Date(expandedAnswer.created_time).toLocaleString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    },
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Metlas Intelligence — floating launcher + chat (admin, overview only) */}
      {canUseMetlas && view === "overview" && (
        <MetlasIntelligencePanel
          programCode={programFilter === "all" ? null : programFilter}
        />
      )}

      {/* Feedback Detail Modal */}
      {selectedFeedback && (
        <FeedbackDetailModal
          feedback={selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
          onSuccess={() => {
            setSelectedFeedback(null);
            // Trigger refresh of feedback dashboard if it's the current view
            if (view === "student-support") {
              window.location.reload();
            }
          }}
        />
      )}
    </div>
  );
};

export default AdvisorAdmin;
